import { motion } from 'framer-motion'
import {
  Users, MessageSquare, TrendingUp, Award, Heart, ArrowRight,
  Calendar, Sparkles
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from '../i18n/Link'
import SEO, { schemas } from '../components/SEO'
import { useApi } from '../hooks/useApi'
import { communityApi } from '../lib/api'

const statValues = ['5K+', '1K+', '50+', '4.9']

const GithubIcon = () => (
  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
  </svg>
)

const TwitterIcon = () => (
  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
)

const channelMeta = [
  {
    icon: MessageSquare,
    members: '2.5K',
    color: 'from-indigo-500 to-purple-500',
    link: '#'
  },
  {
    icon: GithubIcon,
    members: '500+',
    color: 'from-gray-600 to-gray-800',
    link: '#',
    isCustomIcon: true
  },
  {
    icon: TwitterIcon,
    members: '3K',
    color: 'from-blue-400 to-blue-600',
    link: '#',
    isCustomIcon: true
  },
  {
    icon: Users,
    members: '1.5K',
    color: 'from-primary to-secondary',
    link: '#'
  },
]

const eventTimes = ['14:00', '18:00']

export default function Community() {
  const { t } = useTranslation(['community', 'common'])
  const { data: communityData } = useApi(() => communityApi.getAll(), [])
  const discussions = communityData?.posts || []

  const statLabels = t('stats', { returnObjects: true }) as string[]
  const stats = statValues.map((value, i) => ({ value, label: statLabels[i] }))

  const channelTexts = t('channels.items', { returnObjects: true }) as { name: string; description: string }[]
  const channels = channelMeta.map((meta, i) => ({ ...meta, ...channelTexts[i] }))

  const eventTexts = t('events.items', { returnObjects: true }) as { title: string; date: string; type: string }[]
  const events = eventTexts.map((e, i) => ({ ...e, time: eventTimes[i] }))

  const guidelines = t('guidelines.items', { returnObjects: true }) as string[]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}

      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/community"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.community'), url: '/community' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Users className="w-4 h-4" />
            {t('hero.eyebrow')}
          </span>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            {t('hero.titleStart')}{' '}
            <span className="text-gradient">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('hero.text')}
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="glass rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-gradient mb-2">{stat.value}</div>
              <div className="text-gray-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Channels */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h2 className="text-3xl font-display font-bold text-white text-center mb-12">
            {t('channels.title')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {channels.map((channel, index) => (
              <motion.a
                key={channel.name}
                href={channel.link}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-6 group hover:bg-white/10 transition-all"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${channel.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  {channel.isCustomIcon ? (
                    <channel.icon />
                  ) : (
                    <channel.icon className="w-7 h-7 text-white" />
                  )}
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-primary transition-colors">
                  {channel.name}
                </h3>
                <p className="text-gray-400 text-sm mb-4">{channel.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-sm">{t('channels.members', { members: channel.members })}</span>
                  <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-primary transition-colors" />
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Discussions */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6 mb-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  {t('discussions.title')}
                </h2>
                <Link to="#" className="text-primary text-sm hover:underline">
                  {t('discussions.viewAll')}
                </Link>
              </div>
              <div className="space-y-4">
                {discussions.map((discussion) => (
                  <div
                    key={discussion.title}
                    className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group"
                  >
                    <h3 className="text-white font-medium mb-2 group-hover:text-primary transition-colors">
                      {discussion.title}
                    </h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-gray-500 text-sm">{discussion.author}</span>
                        <span className="flex items-center gap-1 text-gray-500 text-sm">
                          <MessageSquare className="w-4 h-4" />
                          {discussion._count?.replies || 0}
                        </span>
                        <span className="flex items-center gap-1 text-gray-500 text-sm">
                          <Heart className="w-4 h-4" />
                          {discussion.likes}
                        </span>
                      </div>
                      <span className="text-gray-500 text-xs">{t('discussions.views', { count: discussion.viewCount })}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Contributors */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Award className="w-5 h-5 text-accent" />
                {t('contributors.title')}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { name: 'Ali K.', avatar: 'AK', contributions: 156 },
                  { name: 'Zeynep M.', avatar: 'ZM', contributions: 132 },
                  { name: 'Can B.', avatar: 'CB', contributions: 98 },
                  { name: 'Elif S.', avatar: 'ES', contributions: 87 },
                  { name: 'Murat T.', avatar: 'MT', contributions: 76 },
                  { name: 'Ayşe D.', avatar: 'AD', contributions: 64 },
                ].map((contributor) => (
                  <div
                    key={contributor.name}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-sm font-bold">
                      {contributor.avatar}
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">{contributor.name}</div>
                      <div className="text-gray-500 text-xs">{t('contributors.contributions', { count: contributor.contributions })}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Events */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                {t('events.title')}
              </h3>
              <div className="space-y-4">
                {events.map((event) => (
                  <div key={event.title} className="p-4 rounded-xl bg-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">
                        {event.type}
                      </span>
                    </div>
                    <h4 className="text-white font-medium mb-2">{event.title}</h4>
                    <div className="flex items-center gap-4 text-gray-500 text-sm">
                      <span>{event.date}</span>
                      <span>{event.time}</span>
                    </div>
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 py-2 text-primary text-sm font-medium hover:underline">
                {t('events.viewAll')}
              </button>
            </motion.div>

            {/* Join CTA */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6 text-center"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 border border-primary/20 rounded-full mb-4">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-primary text-sm">{t('join.eyebrow')}</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('join.title')}
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                {t('join.text')}
              </p>
              <button className="w-full py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-xl transition-colors">
                {t('join.button')}
              </button>
            </motion.div>

            {/* Guidelines */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-4">{t('guidelines.title')}</h3>
              <ul className="space-y-2 text-sm">
                {guidelines.map((rule) => (
                  <li key={rule} className="flex items-start gap-2 text-gray-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                    {rule}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
